'use client';

import posthog from 'posthog-js';
import { useEffect, type ReactNode } from 'react';

/**
 * Reports a route error boundary's error the way every boundary does: to the
 * console and to PostHog, once per distinct error.
 */
export function useCaptureRouteError(error: Error & { digest?: string }) {
  useEffect(() => {
    console.error(error);
    posthog.captureException(error);
  }, [error]);
}

/**
 * The fallback a route error boundary renders: a heading, an explanation and
 * an actions row the caller fills, normally with a RetryButton wired to the
 * boundary's `reset`.
 */
export function RouteErrorFallback({
  title,
  description,
  children
}: {
  title: ReactNode;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <p className="max-w-md text-center text-sm text-muted-foreground">
        {description}
      </p>
      <div className="flex gap-3">{children}</div>
    </div>
  );
}

/** The primary action of a route error fallback. */
export function RetryButton({
  onClick,
  children
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-700"
    >
      {children}
    </button>
  );
}
